import React from 'react'
import { connect, useDispatch } from 'react-redux'
import { compose } from 'recompose'
import Immutable from 'immutable'
import classNames from 'classnames'
import { FixedSizeList as List } from 'react-window'

import { providesChannelMemberships } from 'components/Channels/dataSync/channelMembers'
import { normalize } from 'helpers/StringHelper'
import { getNote } from 'data/notes/selectors'
import { AddUserIcon, SearchIcon } from 'components/Utils/Icons'
import { selectors as ChannelsSelectors } from 'data/channels'
import { setActiveModal } from 'data/ui/actions'
import { INVITE_OAUTH_MEMBERS } from 'components/Utils/Modal/constants'
import { selectors as UsersSelectors } from 'data/users'
import { isPublic } from 'data/channels/record'

import './style.css'
import EmptyStateIllustration from './EmptyState.svg'
import NoResultsIllustration from './NoResults.svg'

import { Button } from 'components/base'

import DropdownUserItem from './UserItem'
import { measureTimingEnd } from 'data/metrics'

const HEIGHT = 400
const WIDTH = 280
const HEIGHT_SEARCH = 44

function getRows({ channel, users, channelMembers, currentUser }) {
  const usersWithAccess = users.reduce((dictionnary, user) => {
    // Exclude current user
    if (user.get('id') === currentUser.id) {
      return dictionnary
    }

    // Identify users without channel permission
    const hasAccess =
      isPublic(channel) || channelMembers.find(member => member.id === user.id)

    return dictionnary.concat({
      user,
      hasAccess,
    })
  }, [])

  return usersWithAccess.sort(function compareAccess(rowa, rowb) {
    // Users with access are first
    if (rowa.hasAccess && !rowb.hasAccess) {
      return -1
    }
    if (!rowa.hasAccess && rowb.hasAccess) {
      return 1
    }
    // By default, sort by username
    return rowa.user.username.localeCompare(rowb.user.get('username'))
  })
}

function getFilteredRows({ rows, inputValue }) {
  // To avoid confusions with the behavior in editor
  // let's remove the `@` sign in case the user inputs it.
  inputValue = inputValue.replace(/^@/, '')

  const filteredRows = rows.filter(({ user }) => {
    return (
      user &&
      [
        normalize(user.displayName),
        normalize(user.username),
        normalize(user.email),
      ].some(element => element.includes(normalize(inputValue)))
    )
  })

  return filteredRows.length ? filteredRows : []
}

const ENTER = 13
const UP = 38
const DOWN = 40
function SearchInput({
  setInputValue,
  inputValue,
  moveUp,
  moveDown,
  moveOut,
  moveTop,
  commit,
}) {
  const onKeyDown = React.useCallback(
    event => {
      if (event.keyCode === UP) {
        event.preventDefault()
        return moveUp()
      }
      if (event.keyCode === DOWN) {
        event.preventDefault()
        return moveDown()
      }
      if (event.keyCode === ENTER) {
        event.preventDefault()
        return commit()
      }
    },
    [moveUp, moveDown, commit]
  )
  const onChange = React.useCallback(
    event => {
      const { value } = event.target
      setInputValue(value)
      if (value.length > 0) {
        moveTop()
      } else {
        moveOut()
      }
    },
    [setInputValue, moveTop, moveOut]
  )

  return (
    <div
      style={{ height: HEIGHT_SEARCH }}
      className=" flexbox border-bottom-medium border-shade-50 flexbox-align-end"
    >
      <div className="flexbox-no-grow padding-normal shade-30">
        <SearchIcon size={16} />
      </div>
      <div className="flexbox-grow">
        <input
          className="input font-size-normal shade-05 width-full border-none background-transparent padding-vertical-normal padding-right-normal"
          type="text"
          autoFocus
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          tabIndex="-1"
          value={inputValue}
          placeholder="Ping members on this doc"
          onChange={onChange}
          onKeyDown={onKeyDown}
        />
      </div>
    </div>
  )
}

function EmptyState() {
  const dispatch = useDispatch()
  return (
    <div className="flexbox flexbox-column flexbox-align-center padding-largest">
      <img
        style={{ width: 89, height: 127 }}
        src={EmptyStateIllustration}
        alt="no-users"
      />
      <span className="bold margin-top-large margin-bottom-normal">
        Ping your team
      </span>
      <span className="center-text shade-20 margin-bottom-medium">
        Send someone a notification to get their attention on this doc
      </span>
      <Button.Primary
        onClick={() =>
          dispatch(
            setActiveModal({
              id: INVITE_OAUTH_MEMBERS,
              analytics: {
                type: 'INVITE_FROM_PING',
              },
            })
          )
        }
      >
        <div className="flexbox flexbox-align-center">
          <AddUserIcon className="margin-right-small" />
          <span className="bold">Add people to Slite</span>
        </div>
      </Button.Primary>
    </div>
  )
}

function NoResults() {
  return (
    <div className="flexbox flexbox-column flexbox-align-center padding-vertical-huge">
      <img src={NoResultsIllustration} alt="no-results" />
      <span className="bold margin-top-large margin-bottom-normal">
        No users match your search term
      </span>
      <span className="center-text shade-20 margin-bottom-medium" />
    </div>
  )
}

function PingInner({
  nudgedUsers,
  showEmptyState,
  channel,
  users,
  channelMembers,
  currentUser,
  nudge,
}) {
  const [inputValue, setInputValue] = React.useState('')
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1)
  const listRef = React.useRef()

  React.useLayoutEffect(() => {
    requestIdleCallback(() => {
      measureTimingEnd('TIME_TO_OPEN_PING')
    })
  }, [])

  const rows = React.useMemo(
    () => {
      return getFilteredRows({
        rows: getRows({ channel, users, channelMembers, currentUser }),
        inputValue,
      })
    },
    [channel, users, channelMembers, currentUser, inputValue]
  )

  const hasRows = rows.length > 0

  const Row = React.useCallback(
    ({ data, index, style }) => {
      const { user, hasAccess } = data[index]
      return (
        <DropdownUserItem
          style={style}
          user={user}
          hasAccess={hasAccess}
          isNudged={!!nudgedUsers[user.id]}
          isFocused={highlightedIndex === index}
          nudge={nudge}
        />
      )
    },
    [nudgedUsers, nudge, highlightedIndex]
  )

  const moveTop = React.useCallback(() => {
    setHighlightedIndex(0)
  }, [])

  const moveOut = React.useCallback(() => {
    setHighlightedIndex(-1)
  }, [])

  const moveUp = React.useCallback(
    () => {
      setHighlightedIndex(highlightedIndex > 0 ? highlightedIndex - 1 : 0)
    },
    [highlightedIndex]
  )

  const moveDown = React.useCallback(
    () => {
      setHighlightedIndex(
        highlightedIndex < rows.length - 1
          ? highlightedIndex + 1
          : rows.length - 1
      )
    },
    [highlightedIndex, rows.length]
  )

  const commit = React.useCallback(
    () => {
      const row = rows[highlightedIndex]
      if (!row) {
        return
      }
      const { user, hasAccess } = row
      if (!hasAccess) {
        return
      }
      nudge(user)
    },
    [nudge, rows, highlightedIndex]
  )

  // Scroll to current highlightedIndex
  React.useEffect(
    () => {
      if (listRef && listRef.current) {
        listRef.current.scrollToItem(highlightedIndex)
      }
    },
    [highlightedIndex]
  )

  return (
    <div
      style={{
        height: HEIGHT,
        width: WIDTH,
      }}
      className={classNames([
        'radius-normal',
        'background-shade-100 border-none',
        'shadow-hovering',
      ])}
    >
      {showEmptyState ? (
        <EmptyState />
      ) : (
        <div className="height-full">
          <SearchInput
            setInputValue={setInputValue}
            inputValue={inputValue}
            moveUp={moveUp}
            moveDown={moveDown}
            moveOut={moveOut}
            moveTop={moveTop}
            commit={commit}
          />
          {!hasRows && <NoResults />}

          <List
            ref={listRef}
            height={HEIGHT - HEIGHT_SEARCH}
            width={WIDTH}
            itemSize={40}
            itemCount={rows.length}
            itemData={rows}
            style={{ overflowX: 'hidden' }}
          >
            {Row}
          </List>
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state, { noteId }) => {
  const note = getNote(state, noteId)
  const channelId = note.channelId
  const channel = ChannelsSelectors.getChannel(state, channelId)
  const channelMembers = ChannelsSelectors.listUsersWithAccessToChannel(
    state,
    channelId
  )
  const usersCount = UsersSelectors.getAllUsers(state).size
  const currentUser = UsersSelectors.getCurrentUser(state)

  const users = !channel
    ? Immutable.Map()
    : UsersSelectors.getAllUsers(state, { filterOutArchived: true })

  return {
    channelId,
    note,
    channel,
    users,
    currentUser,
    channelMembers,
    showEmptyState: usersCount === 1,
  }
}

const enhance = compose(
  connect(mapStateToProps),
  providesChannelMemberships
)

export default enhance(PingInner)
